import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import {
  GSInfoGroup,
  GSInput,
  GSSelect,
  GSToggle,
  defaultBorders,
  defaultGapping,
  defaultLayouts,
  defaultPadding,
  defaultTypography,
  golfstatusColors,
} from "golfstatus_react_components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPen, faCircleNotch } from "@fortawesome/free-solid-svg-icons";
import "./tournament-order-form-response-answer-item.scss";
import {
  buildResponseTarget,
  isAnswered,
  toSelectOption,
} from "../../../helpers/CustomFieldResponses";
import {
  createCustomFieldResponse,
  updateCustomFieldResponse,
} from "../../../reducers/customFieldResponsesSlice";

const baseCardStyle = {
  ...defaultLayouts.hStack,
  ...defaultLayouts.fullWidth,
  ...defaultGapping.mediumGap,
  ...defaultPadding.xLargePad,
  ...defaultBorders.mediumLargeBorderRadius,
  alignItems: "center",
  justifyContent: "space-between",
};

const cardStyle = (background) => ({ ...baseCardStyle, background });

const emptyCardStyle = {
  ...baseCardStyle,
  boxSizing: "border-box",
  border: `2px dashed ${golfstatusColors.grey200}`,
};

const editingCardStyle = {
  ...defaultLayouts.vStack,
  ...defaultLayouts.fullWidth,
  ...defaultGapping.largeGap,
  ...defaultPadding.xLargePad,
  ...defaultBorders.mediumLargeBorderRadius,
  background: golfstatusColors.grey100,
};

const valuePrimaryStyle = {
  ...defaultTypography.bodyRegular,
  color: golfstatusColors.white,
};
const valueSecondaryStyle = {
  ...defaultTypography.body,
  color: golfstatusColors.white,
  opacity: 1,
};
const editNameStyle = { ...defaultTypography.bodyRegular };
const editLabelStyle = {
  ...defaultTypography.overline2,
  ...defaultTypography.uppercase,
  opacity: 0.7,
};
const editFieldStyle = {
  ...defaultLayouts.vStack,
  ...defaultLayouts.fullWidth,
  ...defaultGapping.mediumGap,
};
const emptyPrimaryStyle = { ...defaultTypography.bodyRegular.withOpacity(0.7) };
const emptySecondaryStyle = {
  ...defaultTypography.bodyRegular,
  color: golfstatusColors.grey800,
  opacity: 0.7,
};

const getInfoGroup = (primary, secondary) => [
  {
    fit: "stretch",
    sections: [
      {
        sectionItems: [
          { type: "full-width", ...primary },
          { type: "full-width", ...secondary },
        ],
      },
    ],
  },
];

const TournamentOrderFormResponseAnswerItem = (props) => {
  const {
    record,
    response,
    question,
    editingKey,
    onEdit,
    commitRef,
    onSaved,
    onUnsaved,
  } = props;

  const dispatch = useDispatch();
  const itemKey = `${question?.id}-${record?.id}`;
  const initialValue = response?.value ?? "";
  const editing = editingKey === itemKey;
  const [draft, setDraft] = useState(initialValue);
  const [saving, setSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  const [hover, setHover] = useState(false);
  const savingRef = useRef(false);
  const mountedRef = useRef(true);
  const saveRef = useRef();

  useEffect(() => () => (mountedRef.current = false), []);

  useEffect(() => {
    if (!justSaved) {
      return undefined;
    }
    const timeout = setTimeout(() => {
      if (mountedRef.current) {
        setJustSaved(false);
      }
    }, 2000);
    return () => clearTimeout(timeout);
  }, [justSaved]);

  const answered = isAnswered({ response }) && response.value !== "";

  const normalizeValue = (value) =>
    `${value ?? ""}`.trim() === "" ? "" : `${value}`;
  const canClear =
    !question?.required && Boolean(response?.id) && initialValue !== "";
  const hasSaveableChange = (value) => {
    const next = normalizeValue(value);
    if (next === initialValue) {
      return false;
    }
    return next !== "" || canClear;
  };

  const displayFor = (value) => {
    if (question?.fieldType === "checkbox") {
      return value === "true" ? "Yes" : "No";
    }
    return value;
  };

  const savingDisplayValue =
    normalizeValue(draft) === "" ? "No Response" : displayFor(draft);

  const pending = !editing && !saving && hasSaveableChange(draft);
  const pendingClear = pending && normalizeValue(draft) === "";
  const showAnswered = !pendingClear && (answered || pending);
  const displayValue = pending
    ? displayFor(draft)
    : answered
    ? displayFor(response.value)
    : "No Response";

  const startEditing = () => {
    setHover(false);
    onUnsaved?.(itemKey, null);
    onEdit?.(itemKey);
  };

  const save = async (rawValue) => {
    if (savingRef.current) {
      return false;
    }
    const value = normalizeValue(rawValue);
    if (!hasSaveableChange(value)) {
      if (editing) {
        onEdit?.(null);
      }
      return true;
    }
    savingRef.current = true;
    setDraft(value);
    setHover(false);
    if (editing) {
      onEdit?.(null);
    }
    setSaving(true);
    const thunk = response?.id
      ? updateCustomFieldResponse({ id: response.id, value })
      : createCustomFieldResponse({
          registrationCustomField: { id: question?.id },
          value,
          ...buildResponseTarget(record),
        });
    const result = await dispatch(thunk);
    savingRef.current = false;
    if (mountedRef.current) {
      setSaving(false);
    }
    const ok = result?.meta?.requestStatus === "fulfilled";
    if (ok) {
      onUnsaved?.(itemKey, null);
      if (mountedRef.current) {
        setJustSaved(true);
      }
      onSaved?.();
    } else {
      onUnsaved?.(itemKey, {
        save: () => saveRef.current(value),
        discard: () => setDraft(initialValue),
      });
    }
    return ok;
  };
  saveRef.current = save;

  if (editing && commitRef) {
    commitRef.current = () => {
      if (hasSaveableChange(draft)) {
        const value = normalizeValue(draft);
        onUnsaved?.(itemKey, {
          save: () => saveRef.current(value),
          discard: () => {
            setDraft(initialValue);
            onEdit?.(null);
          },
        });
        return true;
      }
      return false;
    };
  }

  const getControl = () => {
    if (question?.fieldType === "select") {
      const baseOptions = (question?.options ?? []).map(toSelectOption);
      const options = question?.required
        ? baseOptions
        : [{ label: "No Response", value: "" }, ...baseOptions];
      const selectedOption =
        options.find((option) => `${option.value}` === `${response?.value}`) ??
        (response?.value
          ? { label: `${response.value}`, value: response.value }
          : null);
      return (
        <GSSelect
          options={options}
          selectedOption={selectedOption}
          placeholder="Select a response..."
          onChange={(option) => save(option?.value)}
        />
      );
    }

    if (question?.fieldType === "checkbox") {
      const checked = response?.value === "true";
      return (
        <GSToggle
          value={checked}
          trueDescription="Yes"
          falseDescription="No"
          onClick={() => save(`${!checked}`)}
        />
      );
    }

    const isSaveDisabled = !hasSaveableChange(draft);
    return (
      <GSInput
        type={question?.fieldType === "number" ? "number" : "text"}
        style={{ width: "100%" }}
        textValue={draft}
        placeholder="Enter a response..."
        onChange={(event) => setDraft(event.target.value)}
        onSubmit={(value) => save(value)}
        rightIconClick={() => save(draft)}
        rightButtonProps={{
          type: "black secondary",
          title: "Save",
          isDisabled: isSaveDisabled,
        }}
      />
    );
  };

  if (saving) {
    return (
      <tournament-order-form-response-answer-item style={{ display: "block" }}>
        <div style={cardStyle(golfstatusColors.cyan800)}>
          <GSInfoGroup
            dataGroups={getInfoGroup(
              { value: record?.name, sectionItemStyle: valuePrimaryStyle },
              { value: savingDisplayValue, sectionItemStyle: valueSecondaryStyle }
            )}
          />
          <FontAwesomeIcon
            icon={faCircleNotch}
            spin={true}
            style={{ color: golfstatusColors.white }}
          />
        </div>
      </tournament-order-form-response-answer-item>
    );
  }

  if (editing) {
    return (
      <tournament-order-form-response-answer-item style={{ display: "block" }}>
        <div data-editing-answer="true" style={editingCardStyle}>
          <div style={editNameStyle}>{record?.name}</div>
          <div style={editFieldStyle}>
            <div style={editLabelStyle}>
              {question?.name}
              {question?.required && " *"}
            </div>
            <div className="answer-item-edit-control">{getControl()}</div>
          </div>
        </div>
      </tournament-order-form-response-answer-item>
    );
  }

  const clickableStyle = showAnswered
    ? {
        ...cardStyle(
          hover || justSaved
            ? golfstatusColors.cyan800
            : golfstatusColors.cyan700
        ),
        transition: "background 0.3s ease",
        cursor: "pointer",
      }
    : {
        ...emptyCardStyle,
        backgroundColor: hover ? golfstatusColors.grey50 : "transparent",
        transition: "background 0.3s ease",
        cursor: "pointer",
      };
  const infoGroup = showAnswered
    ? getInfoGroup(
        { value: record?.name, sectionItemStyle: valuePrimaryStyle },
        { value: displayValue, sectionItemStyle: valueSecondaryStyle }
      )
    : getInfoGroup(
        { value: record?.name, sectionItemStyle: emptyPrimaryStyle },
        { value: "No Response", sectionItemStyle: emptySecondaryStyle }
      );

  return (
    <tournament-order-form-response-answer-item style={{ display: "block" }}>
      <div
        style={clickableStyle}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        onClick={startEditing}
      >
        <GSInfoGroup dataGroups={infoGroup} />
        <FontAwesomeIcon
          icon={faPen}
          style={{
            color: showAnswered ? golfstatusColors.white : golfstatusColors.black,
          }}
        />
      </div>
    </tournament-order-form-response-answer-item>
  );
};

export default TournamentOrderFormResponseAnswerItem;
